import React, {useContext, useEffect, useRef, useState} from "react";
import {Cancel, Save} from "@mui/icons-material";

import useEditVideo from "../edit/useEditVideo";
import {AppbarContentContext} from "../../../../commons/routing/appbar/AppbarContentProvider";
import AppbarActionButton from "../../../../commons/elements/buttons/AppbarActionButton";
import {useParams} from "react-router-dom";
import GeneralEditVideoForm from "../utils/GeneralEditVideoForm";
import ScoredScenesSection from "../../videosScored/overview/ScoredScenesSection";
import AppButton from "../../../../commons/elements/buttons/AppButton";
import AddIcon from "@mui/icons-material/Add";
import useLoadVideo from "../utils/useLoadVideo";
import LoadingProgressIndication from "../../../../commons/elements/progress/loading/LoadingProgressIndication";
import LastUpdateInfo from "../../../../commons/utils/lastUpdate/LastUpdateInfo";
import {useIntl} from "react-intl";
import {FirebaseAuthContext} from "../../../../commons/firebase/FirebaseAuthProvider";

const EditVideoSection = () => {
    // ... the actual data is loaded based on the URL key (which is however the same as the key in the selectedVideo)
    const {key} = useParams()

    const {video, loading} = useLoadVideo(key)

    // Hook to update the appbar
    const {setCurrentAppbarContent} = useContext(AppbarContentContext)

    // Check if we are in the view-only mode - in this case, the creation button can always be enabled (as no editing is possible and the video form is valid)
    const {isOperationOnObjectAllowed} = useContext(FirebaseAuthContext);


    // Hook providing the save user functionality
    const {
        saveVideo,
        uploadAndSaveVideo,
        saveVideoAndDefineScores,
        saveVideoAndEditScores,
        cancel,
        saveInProgress,
        checkInProgress
    } = useEditVideo()

    // The scored scenes, which are related to the selected video key
    const [associatedScoredScenes, setAssociatedScoredScenes] = useState()

    // Reference, which holds will hold the user data according to the current form data (from the general user component)
    const editedVideoRef = useRef()

    // Determines if the save button should be enabled.
    const [saveButtonEnabled, setSaveButtonEnabled] = useState(false)

    // Internationalization for simple strings
    const intl = useIntl();

    // Setting the appbar content
    useEffect(() => {
        setCurrentAppbarContent({
            header: intl.formatMessage({
                id: 'app.videos.edit.appbar.header',
                defaultMessage: 'Edit Video'
            }),
            description: <LastUpdateInfo metadata={video?.metadata}/>,
            actions: <>
                <AppbarActionButton
                    label={intl.formatMessage({
                        id: 'app.videos.edit.appbar.actions.save-video',
                        defaultMessage: 'Create'
                    })}
                    onClick={() => uploadAndSaveVideo(editedVideoRef.current)}
                    icon={<Save/>}
                    disabled={!saveButtonEnabled}/>
                <AppbarActionButton
                    label={intl.formatMessage({
                        id: 'app.videos.edit.appbar.actions.save',
                        defaultMessage: 'Save'
                    })}
                    onClick={() => saveVideo(editedVideoRef.current)}
                    icon={<Save/>}
                    disabled={!saveButtonEnabled}/>
                <AppbarActionButton
                    label={intl.formatMessage({
                        id: 'app.videos.edit.appbar.actions.cancel',
                        defaultMessage: 'Cancel'
                    })}
                    onClick={() => cancel()}
                    icon={<Cancel/>}/>
                {video?.vimeo?.transcoded &&
                    <AppbarActionButton
                        label={intl.formatMessage({
                            id: 'app.videos.edit.appbar.actions.saveAndCreateScene',
                            defaultMessage: 'Save & create scene'
                        })}
                        onClick={() => saveVideoAndDefineScores(editedVideoRef.current, !isOperationOnObjectAllowed(video))}
                        disabled={!saveButtonEnabled && isOperationOnObjectAllowed(video)}
                        icon={<AddIcon/>}/>}
            </>,
            progress: checkInProgress || saveInProgress
        })
    }, [saveButtonEnabled, video, checkInProgress, saveInProgress])

    if (saveInProgress) {
        return <LoadingProgressIndication
            text={intl.formatMessage({
                id: 'app.videos.edit.save.loading',
                defaultMessage: 'Saving Video Data...'
            })}/>
    }

    if (loading) {
        return <LoadingProgressIndication
            text={intl.formatMessage({
                id: 'app.videos.edit.loading',
                defaultMessage: 'Loading video...'
            })}/>
    }

    return (
        <>
            {/* Edit video form */}
            <GeneralEditVideoForm selectedVideo={video}
                                  associatedScoredScenes={associatedScoredScenes}
                                  editedVideoRef={editedVideoRef}
                                  setSaveButtonEnabled={setSaveButtonEnabled}/>

            {/* Related scored scene of the current video in case some are already defined */}
            {video.vimeo.transcoded && <div>
                <ScoredScenesSection videoKey={video.key}
                                     onEdit={(selectedScoredScene) => saveVideoAndEditScores(editedVideoRef.current, selectedScoredScene, !isOperationOnObjectAllowed(video))}
                                     onScoredScenesUpdated={(scenes) => {
                                         setAssociatedScoredScenes(scenes)
                                     }}
                                     editButtonEnabled={saveButtonEnabled || !isOperationOnObjectAllowed(video)}/>
                <AppButton
                    label={intl.formatMessage({
                        id: 'app.videos.edit.scoredScenesTable.actions.saveAndCreateScene',
                        defaultMessage: 'Save & create scene'
                    })}
                    onClick={() => saveVideoAndDefineScores(editedVideoRef.current, !isOperationOnObjectAllowed(video))}
                    disabled={!saveButtonEnabled && isOperationOnObjectAllowed(video)}
                    icon={<AddIcon/>}/>
            </div>}
        </>
    );
}
export default EditVideoSection
